import { $, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';

/**
 * The lyrics page.
 *
 * Two clocks, and that is the whole design. The server is asked every few
 * seconds what is playing and where it has got to; between those answers the
 * page counts on its own, so the highlighted line moves smoothly rather than
 * jumping once per poll. A poll every four seconds with a local second hand
 * costs a fraction of what a socket would, for a page somebody leaves open.
 */

interface SyncedLine {
  at: number;
  text: string;
}

interface Answer {
  playing: boolean;
  enabled: boolean;
  mode: 'synced' | 'static';
  paused?: boolean;
  position?: number;
  found?: boolean;
  track?: { title: string; author: string; duration: number };
  lyrics?: {
    title: string;
    artist: string;
    album: string;
    instrumental: boolean;
    plain: string;
    synced: SyncedLine[];
  } | null;
}

/** Often enough that a skip is noticed, rarely enough to be free. */
const POLL_MS = 4_000;

/** How often the highlighted line is reconsidered, between two answers. */
const TICK_MS = 200;

/**
 * How far the page may be from the server before it snaps back.
 *
 * Below this it keeps counting on its own: an answer is always a little old by
 * the time it arrives, and writing it in verbatim every four seconds pulled the
 * highlight backwards each time — which is what put it a line behind the music.
 */
const RESYNC_ABOVE_S = 0.75;

/**
 * The line being sung at a moment, or `-1` before the first one.
 *
 * The last line whose timestamp has passed — not the nearest, which would jump
 * forward during a long instrumental and light up words nobody is singing yet.
 * Exported for the test: this is the one piece of the page with no browser in
 * it and three ways to be wrong.
 */
export function lineIndexAt(lines: readonly SyncedLine[], seconds: number): number {
  let found = -1;

  for (const [index, line] of lines.entries()) {
    if (line.at > seconds) break;
    found = index;
  }

  return found;
}

export function initMusicLyricsPage(): void {
  const page = $<HTMLElement>('[data-lyrics-page]');
  if (!page) return;

  /*
   * The address to ask, given by the page rather than built here.
   *
   * Two pages draw this sheet: the server's own, behind the dashboard, and the
   * public one anybody can open with a link. They differ in exactly one thing —
   * where the answer comes from — so that one thing travels with the page and
   * the rest of this file does not know which of the two it is on.
   */
  const { endpoint, mode } = pageData<{
    endpoint: string;
    mode: 'synced' | 'static';
    enabled: boolean;
  }>();

  const title = page.querySelector<HTMLElement>('[data-lyrics-title]');
  const author = page.querySelector<HTMLElement>('[data-lyrics-author]');
  const stateLabel = page.querySelector<HTMLElement>('[data-lyrics-state-label]');
  const state = page.querySelector<HTMLElement>('[data-lyrics-state]');
  const empty = page.querySelector<HTMLElement>('[data-lyrics-empty]');
  const list = page.querySelector<HTMLElement>('[data-lyrics-lines]');
  const plain = page.querySelector<HTMLElement>('[data-lyrics-plain]');
  const source = page.querySelector<HTMLElement>('[data-lyrics-source]');

  /*
   * The reader's own choice, for this visit.
   *
   * The server's setting decides what the page opens on; switching here changes
   * nothing for anybody else. A view is not a setting — somebody who wants to
   * read ahead of the music should not have to change the server to do it.
   */
  let view: 'synced' | 'static' = mode === 'static' ? 'static' : 'synced';

  /** What is drawn, so a redraw only happens when the song actually changes. */
  let drawn = '';
  let lines: SyncedLine[] = [];
  let paused = false;
  let highlighted = -1;

  /*
   * The clock, anchored rather than accumulated.
   *
   * It used to add a quarter of a second per tick, which assumes a timer that
   * fires exactly on time — and a browser throttles timers, especially while
   * something is scrolling. Every late tick was time the page never counted,
   * so the words fell steadily behind the music between two answers.
   *
   * `performance.now()` is the truth about elapsed time; the anchor is what the
   * server last said, and the position is read from the two.
   */
  let anchorAt = 0;
  let anchorPosition = 0;
  /** Frozen while paused: the track is not advancing, so neither is the sheet. */
  let frozen: number | null = 0;

  const position = (): number => {
    if (frozen !== null) return frozen;
    // Before the first answer there is nothing to count from.
    if (anchorAt === 0) return anchorPosition;

    return anchorPosition + (performance.now() - anchorAt) / 1_000;
  };

  const markMode = () => {
    for (const button of page.querySelectorAll<HTMLButtonElement>('[data-lyrics-mode]')) {
      const mine = button.dataset.lyricsMode === view;
      button.classList.toggle('btn-primary', mine);
      button.classList.toggle('btn-ghost', !mine);
    }
  };

  const show = (which: 'empty' | 'synced' | 'static') => {
    empty?.classList.toggle('hidden', which !== 'empty');
    list?.classList.toggle('hidden', which !== 'synced');
    plain?.classList.toggle('hidden', which !== 'static');
  };

  /** The sheet, drawn once per song: a redraw would lose the scroll position. */
  const drawLines = (sheet: SyncedLine[]) => {
    if (!list) return;

    list.replaceChildren(
      ...sheet.map((line) => {
        const item = document.createElement('li');
        item.className = 'text-subtle transition-colors';
        // An empty line is a pause in the song, and keeps its height so the
        // verses stay apart.
        item.textContent = line.text || ' ';
        return item;
      }),
    );

    highlighted = -1;
  };

  /**
   * The line being sung, moved without redrawing anything else.
   *
   * Scrolled to the middle only when the line changes: calling it every quarter
   * of a second would fight anybody scrolling back to read a verse again.
   */
  const highlight = () => {
    if (!list || lines.length === 0) return;

    const index = lineIndexAt(lines, position());
    if (index === highlighted) return;

    const items = list.children;
    const previous = items[highlighted];
    const current = items[index];

    previous?.classList.remove('text-primary', 'font-semibold');
    previous?.classList.add('text-subtle');

    if (current) {
      current.classList.remove('text-subtle');
      current.classList.add('text-primary', 'font-semibold');
      current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    highlighted = index;
  };

  /** Draws the answer, and says whether a different song is now on screen. */
  const draw = (answer: Answer): boolean => {
    const track = answer.track;
    const words = answer.lyrics ?? null;

    if (title) {
      title.textContent = track
        ? (words?.title ?? track.title) || track.title
        : ct('lyricsIdle', 'Nothing playing.');
    }

    if (author) author.textContent = words?.artist ?? track?.author ?? '';

    if (stateLabel) {
      stateLabel.textContent = !track
        ? ct('lyricsIdle', 'Nothing playing.')
        : answer.paused
          ? ct('playerPaused', 'Paused')
          : ct('playerPlaying', 'Playing');
    }

    // Shown only when there is a state to report: idle, the title says it
    // already, and the badge repeating it crowded the header on a phone.
    state?.classList.toggle('hidden', !track);
    state?.classList.toggle('badge-success', Boolean(track) && !answer.paused);
    state?.classList.toggle('badge-ghost', !track || Boolean(answer.paused));

    paused = Boolean(answer.paused);

    if (!track || !answer.found || !words) {
      show('empty');
      drawn = '';
      lines = [];
      if (source) source.textContent = '';
      return false;
    }

    if (source) {
      source.textContent = [words.album, ct('lyricsSource', 'Lyrics from LRCLIB.')]
        .filter(Boolean)
        .join(' · ');
    }

    const key = `${track.title}|${String(track.duration)}`;
    const timed = words.synced.length > 0;

    /*
     * A synced view of a song nobody has timed falls back to the whole sheet
     * rather than an empty page: the words exist, only the clock does not.
     */
    const showing = view === 'synced' && timed ? 'synced' : 'static';

    const changed = drawn !== `${key}|${showing}`;

    if (changed) {
      lines = words.synced;

      if (showing === 'synced') drawLines(words.synced);
      else if (plain) {
        plain.textContent = words.instrumental
          ? ct('lyricsInstrumental', 'This recording has no words.')
          : words.plain;
      }

      drawn = `${key}|${showing}`;
    }

    show(showing);
    if (showing === 'synced') highlight();

    // A new song starts a new clock: what was counted for the last one says
    // nothing about this one.
    return changed;
  };

  /**
   * What the server says, aged by the trip it took to say it.
   *
   * The answer describes where the track was when the request was **handled**,
   * and it reaches the page a round trip later. Half of that round trip is the
   * fair estimate of the way back, and adding it is the difference between a
   * sheet that follows the music and one that trails it.
   */
  const sync = (answer: Answer, sentAt: number, force: boolean) => {
    const oneWay = (performance.now() - sentAt) / 2_000;
    const said = (answer.position ?? 0) + oneWay;

    /*
     * Paused, the position stops where it is; playing, the page keeps its own
     * count unless it has genuinely drifted — a skip, a seek, a tab that was
     * asleep. Snapping on every answer is what made the highlight step
     * backwards four times a minute.
     */
    if (answer.paused) {
      frozen = said;
      return;
    }

    frozen = null;

    if (force || anchorAt === 0 || Math.abs(said - position()) > RESYNC_ABOVE_S) {
      anchorPosition = said;
      anchorAt = performance.now();
    }
  };

  const refresh = () => {
    const sentAt = performance.now();

    return api
      .get<Answer>(endpoint)
      .then((answer) => {
        /*
         * Drawn first, then the clock: a new song has to be on screen before
         * the position that belongs to it is anchored, and the redraw is
         * exactly what says the song has changed.
         */
        const changed = draw(answer);
        sync(answer, sentAt, changed);
        highlight();
      })
      .catch(() => {
        // A page that cannot reach the server keeps what it is showing: the
        // words on screen are still the words of the song.
      });
  };

  for (const button of page.querySelectorAll<HTMLButtonElement>('[data-lyrics-mode]')) {
    button.addEventListener('click', () => {
      view = button.dataset.lyricsMode === 'static' ? 'static' : 'synced';
      markMode();
      // Forces the redraw the next answer would otherwise skip.
      drawn = '';
      void refresh();
    });
  }

  markMode();
  void refresh();

  const poll = window.setInterval(() => {
    if (!document.hidden) void refresh();
  }, POLL_MS);

  /*
   * The second hand. The server is the truth every four seconds; in between,
   * the page advances the position itself so the highlighted line moves with
   * the music instead of in steps.
   */
  const tick = window.setInterval(() => {
    if (document.hidden || paused || lines.length === 0) return;
    highlight();
  }, TICK_MS);

  window.addEventListener('pagehide', () => {
    window.clearInterval(poll);
    window.clearInterval(tick);
  });
}
